import type { ReactElement } from "react";

import type { MapSummary, Zone, ZoneStatus } from "../../api/types";
import { Badge } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { zoneChipText } from "./ZoneChipBadge";
import { progressPct, type ZoneChip } from "./zoneState";

/** Terrains libres de la licence dans les Données initiales (liste « Terrain » de la carte « Terrain libre », ou groupe
 *  « Mes terrains » de la liste unique quand le terrain libre n'est pas proposé ; « zones » dans le code et l'API) :
 *  chacun avec l'état de sa préparation tant qu'elle n'est pas finie (en file, en préparation, en échec, annulée), lu
 *  dans la liste des zones (relue pendant les préparations) plutôt que dans la liste des cartes, qui n'est relue qu'à
 *  la fin d'une préparation. */

/** Valeur de « Nouveau terrain » (liste « Terrain » de la carte « Terrain libre ») : un choix du brouillon, jamais
 *  enregistré comme carte (le dossier garde sa carte, ou aucune, jusqu'à la préparation du nouveau terrain placé dans
 *  la vue Manœuvre). */
export const FREE_TERRAIN = "\u0000terrain-libre";

export interface ZoneChoice {
  slug: string;
  name: string;
  status: ZoneStatus;
  /** État détaillé (rang, pourcentage), quand la liste des zones le donne. */
  zone: Zone | null;
}

/** Zones à proposer : celles de la liste des cartes (ordre alphabétique du serveur), avec l'état de la liste des
 *  zones quand elle le connaît, puis les zones que la liste des cartes ne connaît pas encore (créées depuis). */
export function zoneChoices(maps: readonly MapSummary[], zones: readonly Zone[] | undefined): ZoneChoice[] {
  const bySlug = new Map((zones ?? []).map((z) => [z.slug, z]));
  const listed = maps
    .filter((m) => m.kind === "user")
    .map((m) => {
      const zone = bySlug.get(m.slug) ?? null;
      return { slug: m.slug, name: zone?.name ?? m.name, status: zone?.status ?? m.status ?? "ready", zone };
    });
  const known = new Set(listed.map((c) => c.slug));
  const added = (zones ?? []).filter((z) => !known.has(z.slug)).map((z) => ({ slug: z.slug, name: z.name, status: z.status, zone: z }));
  return [...listed, ...added];
}

/** Pastille de l'état d'une zone dans la liste, réservée à l'exception (R6) : null pour un terrain prêt (une
 *  extension en cours le laisse prêt). */
export function choiceBadge(choice: ZoneChoice): ReturnType<typeof zoneChipText> | null {
  const chip: ZoneChip | null =
    choice.status === "queued"
      ? { kind: "queued", position: choice.zone?.queue_position ?? null }
      : choice.status === "preparing"
        ? { kind: "preparing", pct: progressPct(choice.zone?.progress, "preparing") }
        : choice.status === "failed"
          ? { kind: "failed" }
          : choice.status === "cancelled"
            ? { kind: "cancelled" }
            : null;
  return chip ? zoneChipText(chip) : null;
}

/** Groupe « Mes terrains » (élément `optgroup`, lu par `Select`) ; null sans terrain. Tous se choisissent : un terrain
 *  prêt porte le dispositif aussitôt, les autres quand leur préparation aboutira. `missing` : terrain du brouillon
 *  inconnu des deux listes (supprimé depuis), affiché « Terrain introuvable » — le bouton désigne alors la liste. */
export function zoneOptionGroup(choices: readonly ZoneChoice[], missing: string | null = null): ReactElement | null {
  if (choices.length === 0 && !missing) return null;
  return (
    <optgroup label={t("Mes terrains")}>
      {choices.map((choice) => {
        const badge = choiceBadge(choice);
        return (
          <option key={choice.slug} value={choice.slug}>
            {choice.name}
            {badge && (
              <>
                {" "}
                <Badge size="sm" dot tone={badge.tone} title={badge.title} className="ml-1">
                  {badge.label}
                </Badge>
              </>
            )}
          </option>
        );
      })}
      {missing && <option value={missing}>{t("Terrain introuvable")}</option>}
    </optgroup>
  );
}
